import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Crosshair,
  Gavel,
  Pencil,
  Plus,
  Quote,
  Trash2,
  X,
  UserPlus,
  Users,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api';
import {
  archiveWitness,
  generatePlanForWitness,
  createStatement,
  createWitness,
  deleteStatement,
  getMatter,
  getWitness,
  listContradictions,
  listMatterDocuments,
  listWitnesses,
  mapWitnessContradictions,
  updateContradiction,
  updateWitness,
  type Contradiction,
  type CrossExamPlanSummary,
  type DocumentSummary,
  type MapContradictionsSummary,
  type PriorStatement,
  type StatementSource,
  type Witness,
  type WitnessDetail,
  type WitnessInput,
  type WitnessRole,
} from '@/lib/api-client';
import type { MatterDetail } from '@/lib/types';

// Phase 10.6 — witness cards. One card per witness on the matter: who they
// are, whose witness they are, what they will say (will-say summary), and the
// prior-statement library beneath (source document, date, the exact quoted
// passage, page/para reference). Feeds contradiction mapping and cross-exam
// plans.

const inputClass =
  'w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none';
const labelClass = 'mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500';

const ROLE_OPTIONS: Array<{ value: WitnessRole; label: string; side: 'ours' | 'theirs' }> = [
  { value: 'plaintiff_witness', label: "Plaintiff's witness", side: 'theirs' },
  { value: 'defendant_witness', label: "Defendant's witness", side: 'theirs' },
  { value: 'hostile', label: 'Hostile witness', side: 'theirs' },
  { value: 'expert', label: 'Expert witness', side: 'theirs' },
  { value: 'character', label: 'Character witness', side: 'theirs' },
  { value: 'own_witness', label: 'Our witness', side: 'ours' },
];

const SOURCE_OPTIONS: StatementSource[] = [
  'affidavit',
  'letter',
  'email',
  'transcript',
  'pleading',
  'media',
  'other',
];

function roleLabel(role: WitnessRole): string {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role;
}

function roleSide(role: WitnessRole): 'ours' | 'theirs' {
  return ROLE_OPTIONS.find((r) => r.value === role)?.side ?? 'theirs';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function Witnesses() {
  const { id: matterId = '' } = useParams<{ id: string }>();
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = useCallback(async () => {
    setWitnesses(await listWitnesses(matterId).catch(() => []));
  }, [matterId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      getMatter(matterId).catch(() => null),
      listWitnesses(matterId).catch(() => []),
      listMatterDocuments(matterId).catch(() => []),
    ]).then(([m, w, d]) => {
      if (!active) return;
      setMatter(m);
      setWitnesses(w);
      setDocs(d);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [matterId]);

  const readyDocs = useMemo(() => docs.filter((d) => d.extractionStatus === 'READY'), [docs]);

  return (
    <main className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 font-display text-3xl italic text-stone-100">
              <Users className="h-6 w-6 text-amber-500" /> Witnesses
            </h1>
            <p className="mt-1 text-sm text-stone-500">{matter?.name ?? '…'}</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowAdd((v) => !v)}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700"
            >
              <UserPlus className="h-4 w-4" /> Add witness
            </button>
            <Link
              to="/matters"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Matters
            </Link>
          </div>
        </div>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        {showAdd ? (
          <WitnessForm
            onSaved={() => {
              setShowAdd(false);
              void refresh();
            }}
            onCancel={() => setShowAdd(false)}
            create={(input) => createWitness(matterId, input)}
          />
        ) : null}

        {loading ? (
          <p className="flex items-center gap-2 text-sm text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading witnesses…
          </p>
        ) : witnesses.length === 0 && !showAdd ? (
          <Card variant="default" padding="md">
            <p className="text-sm text-stone-500">
              No witnesses yet. Add each person who will give evidence, whose witness they are, and
              what they will say — then attach their prior statements so contradiction mapping and
              cross-examination plans have material to work from.
            </p>
          </Card>
        ) : (
          witnesses.map((w) => (
            <WitnessCard key={w.id} witness={w} readyDocs={readyDocs} onChanged={refresh} onError={setError} />
          ))
        )}
      </div>
    </main>
  );
}

// --- witness create/edit form ------------------------------------------------

function WitnessForm({
  initial,
  create,
  onSaved,
  onCancel,
}: {
  initial?: Witness;
  create: (input: WitnessInput) => Promise<unknown>;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [role, setRole] = useState<WitnessRole>(initial?.role ?? 'plaintiff_witness');
  const [affiliation, setAffiliation] = useState(initial?.affiliation ?? '');
  const [importance, setImportance] = useState(initial?.importance ?? 3);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setBusy(true);
    try {
      await create({
        name: name.trim(),
        role,
        affiliation: affiliation.trim() || null,
        importance,
        notes,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the witness.');
      setBusy(false);
    }
  }

  return (
    <Card variant="default" padding="md" className="space-y-4 border-amber-900/30 bg-amber-950/10">
      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
        {initial ? 'Edit witness' : 'New witness'}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="w-name">
            Name
          </label>
          <input
            id="w-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Qingzhu Xu"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="w-role">
            Whose witness
          </label>
          <select
            id="w-role"
            value={role}
            onChange={(e) => setRole(e.target.value as WitnessRole)}
            className={inputClass}
          >
            <optgroup label="Theirs">
              {ROLE_OPTIONS.filter((r) => r.side === 'theirs').map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="Ours">
              {ROLE_OPTIONS.filter((r) => r.side === 'ours').map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="w-affiliation">
            Affiliation / connection to the case
          </label>
          <input
            id="w-affiliation"
            value={affiliation}
            onChange={(e) => setAffiliation(e.target.value)}
            placeholder="e.g. director of the plaintiff"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="w-importance">
            Importance (5 = case-determinative)
          </label>
          <select
            id="w-importance"
            value={importance}
            onChange={(e) => setImportance(Number(e.target.value))}
            className={inputClass}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass} htmlFor="w-notes">
          Will-say summary — what this witness will say
        </label>
        <textarea
          id="w-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Summary of their expected evidence, and what we need from them."
          className={inputClass}
        />
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {initial ? 'Save changes' : 'Add witness'}
        </button>
      </div>
    </Card>
  );
}

// --- witness card -------------------------------------------------------------

function WitnessCard({
  witness,
  readyDocs,
  onChanged,
  onError,
}: {
  witness: Witness;
  readyDocs: DocumentSummary[];
  onChanged: () => Promise<void> | void;
  onError: (msg: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<WitnessDetail | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const side = roleSide(witness.role);

  const loadDetail = useCallback(async () => {
    setDetail(await getWitness(witness.id).catch(() => null));
  }, [witness.id]);

  useEffect(() => {
    if (open && !detail) void loadDetail();
  }, [open, detail, loadDetail]);

  async function archive(): Promise<void> {
    onError(null);
    try {
      await archiveWitness(witness.id);
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not archive the witness.');
    }
  }

  if (editing) {
    return (
      <WitnessForm
        initial={witness}
        create={(input) => updateWitness(witness.id, input)}
        onSaved={() => {
          setEditing(false);
          setDetail(null);
          void onChanged();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 items-start gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-stone-500" />
          ) : (
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-stone-500" />
          )}
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-lg text-stone-100">{witness.name}</span>
              <span
                className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${
                  side === 'ours'
                    ? 'border-emerald-700/50 bg-emerald-950/30 text-emerald-300'
                    : 'border-amber-700/50 bg-amber-950/30 text-amber-300'
                }`}
              >
                {roleLabel(witness.role)}
              </span>
              <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                Importance {witness.importance}/5
              </span>
            </span>
            {witness.affiliation ? (
              <span className="mt-0.5 block truncate text-xs text-stone-500">
                {witness.affiliation}
              </span>
            ) : null}
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.15em] text-stone-600">
            {witness._count?.priorStatements ?? 0} stmts · {witness._count?.crossExamPlans ?? 0}{' '}
            plans
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-stone-500 hover:text-amber-400"
            aria-label="Edit witness"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setConfirmArchive(true)}
            className="text-stone-600 hover:text-red-400"
            aria-label="Archive witness"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {witness.notes ? (
        <p className="whitespace-pre-wrap border-l-2 border-stone-800 pl-3 text-sm text-stone-400">
          {witness.notes}
        </p>
      ) : null}

      {open ? (
        <>
          <StatementsSection
            witnessId={witness.id}
            statements={detail?.priorStatements ?? null}
            readyDocs={readyDocs}
            onChanged={async () => {
              await loadDetail();
              await onChanged();
            }}
            onError={onError}
          />
          <ContradictionsSection
            witnessId={witness.id}
            statements={detail?.priorStatements ?? []}
            onError={onError}
          />
          <PlansSection
            witnessId={witness.id}
            plans={detail?.crossExamPlans ?? []}
            onChanged={async () => {
              await loadDetail();
              await onChanged();
            }}
            onError={onError}
          />
        </>
      ) : null}

      {confirmArchive ? (
        <ConfirmDialog
          title="Archive this witness?"
          message={`${witness.name} and their prior statements will be hidden from the matter (soft delete — nothing is destroyed).`}
          confirmLabel="Archive"
          destructive
          onConfirm={() => {
            setConfirmArchive(false);
            void archive();
          }}
          onCancel={() => setConfirmArchive(false)}
        />
      ) : null}
    </Card>
  );
}

// --- prior statements ----------------------------------------------------------

function StatementsSection({
  witnessId,
  statements,
  readyDocs,
  onChanged,
  onError,
}: {
  witnessId: string;
  statements: PriorStatement[] | null;
  readyDocs: DocumentSummary[];
  onChanged: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  async function remove(id: string): Promise<void> {
    onError(null);
    try {
      await deleteStatement(id);
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not delete the statement.');
    }
  }

  return (
    <div className="space-y-3 border-t border-stone-800/60 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
          Prior statements {statements ? `(${statements.length})` : ''}
        </p>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
        >
          <Plus className="h-3.5 w-3.5" /> Add statement
        </button>
      </div>

      {adding ? (
        <StatementForm
          witnessId={witnessId}
          readyDocs={readyDocs}
          onSaved={async () => {
            setAdding(false);
            await onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : null}

      {statements === null ? (
        <p className="flex items-center gap-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : statements.length === 0 && !adding ? (
        <p className="text-sm text-stone-600">
          No prior statements recorded. Each one needs the source document, the date, the exact
          quoted passage and a page or paragraph reference — that is the impeachment material.
        </p>
      ) : (
        statements.map((s) => (
          <div key={s.id} className="rounded-md border border-stone-800/60 bg-stone-900/40 p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="flex items-center gap-2 text-xs text-stone-400">
                <FileText className="h-3.5 w-3.5 text-stone-500" />
                <span className="uppercase tracking-[0.1em] text-stone-500">{s.source}</span>
                {s.dateMade ? <span>· {fmtDate(s.dateMade)}</span> : null}
                {!s.confirmed ? (
                  <span className="rounded border border-amber-700/50 bg-amber-950/30 px-1.5 text-[10px] uppercase text-amber-300">
                    unconfirmed
                  </span>
                ) : null}
              </p>
              <button
                type="button"
                onClick={() => setDeleteId(s.id)}
                className="text-stone-600 hover:text-red-400"
                aria-label="Delete statement"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <blockquote className="mt-2 flex gap-2 text-sm text-stone-200">
              <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <span className="whitespace-pre-wrap">{s.statementText}</span>
            </blockquote>
            <p className="mt-2 text-xs text-stone-500">{s.sourceCitation || '(no citation recorded)'}</p>
            {s.topic ? <p className="mt-1 text-[11px] uppercase tracking-[0.1em] text-stone-600">Topic: {s.topic}</p> : null}
          </div>
        ))
      )}

      {deleteId ? (
        <ConfirmDialog
          title="Delete this statement?"
          message="The quoted passage and its citation will be permanently removed from the witness card."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            const id = deleteId;
            setDeleteId(null);
            void remove(id);
          }}
          onCancel={() => setDeleteId(null)}
        />
      ) : null}
    </div>
  );
}

function StatementForm({
  witnessId,
  readyDocs,
  onSaved,
  onCancel,
}: {
  witnessId: string;
  readyDocs: DocumentSummary[];
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [source, setSource] = useState<StatementSource>('affidavit');
  const [docFilename, setDocFilename] = useState('');
  const [reference, setReference] = useState('');
  const [dateMade, setDateMade] = useState('');
  const [statementText, setStatementText] = useState('');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    if (!statementText.trim()) {
      setError('The exact quoted passage is required.');
      return;
    }
    if (!docFilename.trim()) {
      setError('The source document is required.');
      return;
    }
    setBusy(true);
    try {
      // Convention: sourceCitation = "<document>, <page/para ref>". The
      // contradiction mapper and the hearing quick action rely on the document
      // name leading the citation.
      const citation = reference.trim()
        ? `${docFilename.trim()}, ${reference.trim()}`
        : docFilename.trim();
      await createStatement(witnessId, {
        source,
        sourceCitation: citation,
        statementText: statementText.trim(),
        topic: topic.trim() || null,
        dateMade: dateMade || null,
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the statement.');
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-amber-900/30 bg-amber-950/10 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="s-doc">
            Source document
          </label>
          <input
            id="s-doc"
            list="s-doc-options"
            value={docFilename}
            onChange={(e) => setDocFilename(e.target.value)}
            placeholder="Pick an uploaded document or type a name"
            className={inputClass}
          />
          <datalist id="s-doc-options">
            {readyDocs.map((d) => (
              <option key={d.id} value={d.filename} />
            ))}
          </datalist>
        </div>
        <div>
          <label className={labelClass} htmlFor="s-ref">
            Page / paragraph reference
          </label>
          <input
            id="s-ref"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. para 14 / p 3"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="s-source">
            Statement type
          </label>
          <select
            id="s-source"
            value={source}
            onChange={(e) => setSource(e.target.value as StatementSource)}
            className={inputClass}
          >
            {SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="s-date">
            Date made
          </label>
          <input
            id="s-date"
            type="date"
            value={dateMade}
            onChange={(e) => setDateMade(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      <div>
        <label className={labelClass} htmlFor="s-text">
          Exact quoted passage (verbatim — never a paraphrase)
        </label>
        <textarea
          id="s-text"
          value={statementText}
          onChange={(e) => setStatementText(e.target.value)}
          rows={3}
          placeholder="Paste the witness's exact words from the document."
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass} htmlFor="s-topic">
          Topic (optional)
        </label>
        <input
          id="s-topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g. valuation methodology"
          className={inputClass}
        />
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add statement
        </button>
      </div>
    </div>
  );
}


// --- contradiction mapping ------------------------------------------------------

function ContradictionsSection({
  witnessId,
  statements,
  onError,
}: {
  witnessId: string;
  statements: PriorStatement[];
  onError: (msg: string | null) => void;
}) {
  const [items, setItems] = useState<Contradiction[] | null>(null);
  const [mapping, setMapping] = useState(false);
  const [summary, setSummary] = useState<MapContradictionsSummary | null>(null);

  const load = useCallback(async () => {
    setItems(await listContradictions(witnessId).catch(() => []));
  }, [witnessId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runMapping(): Promise<void> {
    onError(null);
    setMapping(true);
    setSummary(null);
    try {
      const s = await mapWitnessContradictions(witnessId);
      setSummary(s);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Contradiction mapping failed.');
    } finally {
      setMapping(false);
    }
  }

  async function setStatus(id: string, status: Contradiction['status']): Promise<void> {
    onError(null);
    try {
      await updateContradiction(id, status);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not update the candidate.');
    }
  }

  const statementText = (id: string): string =>
    statements.find((s) => s.id === id)?.statementText ?? '(statement)';

  const active = (items ?? []).filter((c) => c.status !== 'dismissed');
  const dismissed = (items ?? []).filter((c) => c.status === 'dismissed');

  return (
    <div className="space-y-3 border-t border-stone-800/60 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
          Contradictions {items ? `(${active.length})` : ''}
        </p>
        <button
          type="button"
          onClick={() => void runMapping()}
          disabled={mapping || statements.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-700/50 bg-amber-950/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-amber-300 hover:bg-amber-900/30 disabled:opacity-40"
        >
          {mapping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
          {mapping ? 'Searching documents…' : 'Map contradictions'}
        </button>
      </div>

      {statements.length === 0 ? (
        <p className="text-sm text-stone-600">
          Add prior statements first — mapping searches the matter&apos;s documents for passages
          that contradict each one.
        </p>
      ) : null}

      {summary ? (
        summary.noDocuments ? (
          <p className="text-sm text-amber-300">
            This matter has no embedded documents to search. Upload the brief on the Documents page
            first.
          </p>
        ) : (
          <p className="text-sm text-stone-400">
            Scanned {summary.statementsScanned} statement{summary.statementsScanned === 1 ? '' : 's'}{' '}
            against {summary.chunksConsidered} passages — {summary.created} new candidate
            {summary.created === 1 ? '' : 's'} ({summary.verified} verified, {summary.unverified}{' '}
            unverified).
          </p>
        )
      ) : null}

      {active.map((c) => (
        <div
          key={c.id}
          className={`rounded-md border p-3 ${
            c.status === 'accepted'
              ? 'border-emerald-800/60 bg-emerald-950/20'
              : 'border-stone-800/60 bg-stone-900/40'
          }`}
        >
          <p className="text-[11px] text-stone-500">
            Against: <span className="italic">“{statementText(c.priorStatementId).slice(0, 140)}”</span>
          </p>
          <blockquote className="mt-2 flex gap-2 text-sm text-stone-200">
            <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span className="whitespace-pre-wrap">{c.quotedText}</span>
          </blockquote>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-stone-500">
            <FileText className="h-3.5 w-3.5" />
            <span>{c.filename}</span>
            {c.verified ? (
              <span className="rounded border border-emerald-700/50 bg-emerald-950/30 px-1.5 text-[10px] uppercase tracking-[0.1em] text-emerald-300">
                verified verbatim
              </span>
            ) : (
              <span className="rounded border border-red-800/60 bg-red-950/30 px-1.5 text-[10px] uppercase tracking-[0.1em] text-red-300">
                unverified — check the source before use
              </span>
            )}
            {typeof c.similarity === 'number' ? (
              <span className="text-stone-600">sim {c.similarity.toFixed(2)}</span>
            ) : null}
          </div>
          {c.reasoning ? <p className="mt-2 text-xs text-stone-400">{c.reasoning}</p> : null}
          <div className="mt-2 flex items-center gap-3">
            {c.status !== 'accepted' ? (
              <button
                type="button"
                onClick={() => void setStatus(c.id, 'accepted')}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-emerald-400 hover:text-emerald-300"
              >
                <Check className="h-3.5 w-3.5" /> Accept
              </button>
            ) : (
              <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">Accepted</span>
            )}
            <button
              type="button"
              onClick={() => void setStatus(c.id, 'dismissed')}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-red-400"
            >
              <X className="h-3.5 w-3.5" /> Dismiss
            </button>
          </div>
        </div>
      ))}

      {dismissed.length > 0 ? (
        <details className="text-xs text-stone-600">
          <summary className="cursor-pointer uppercase tracking-[0.15em]">
            {dismissed.length} dismissed
          </summary>
          <div className="mt-2 space-y-2">
            {dismissed.map((c) => (
              <div key={c.id} className="rounded border border-stone-800/40 p-2 opacity-60">
                <p className="text-stone-400">“{c.quotedText.slice(0, 160)}” — {c.filename}</p>
                <button
                  type="button"
                  onClick={() => void setStatus(c.id, 'candidate')}
                  className="mt-1 text-[10px] uppercase tracking-[0.15em] text-stone-500 hover:text-amber-400"
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}


// --- cross-exam plans -------------------------------------------------------------

function PlansSection({
  witnessId,
  plans,
  onChanged,
  onError,
}: {
  witnessId: string;
  plans: CrossExamPlanSummary[];
  onChanged: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [objective, setObjective] = useState('');
  const [constraints, setConstraints] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [generating, setGenerating] = useState(false);

  async function generate(): Promise<void> {
    onError(null);
    setGenerating(true);
    try {
      await generatePlanForWitness(witnessId, {
        objective: objective.trim(),
        constraints: constraints.trim() || null,
        maxDuration: maxDuration ? Number(maxDuration) : null,
      });
      setShowForm(false);
      setObjective('');
      setConstraints('');
      setMaxDuration('');
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Plan generation failed.');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-stone-800/60 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
          Cross-exam plans ({plans.length})
        </p>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-700/50 bg-amber-950/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-amber-300 hover:bg-amber-900/30"
        >
          <Gavel className="h-3.5 w-3.5" /> New plan
        </button>
      </div>

      {showForm ? (
        <div className="space-y-3 rounded-md border border-amber-900/30 bg-amber-950/10 p-3">
          <div>
            <label className={labelClass} htmlFor="p-objective">
              The single admission I need from this witness
            </label>
            <textarea
              id="p-objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={2}
              placeholder="e.g. that he never independently verified the valuation figures"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="p-constraints">
                Constraints (optional)
              </label>
              <input
                id="p-constraints"
                value={constraints}
                onChange={(e) => setConstraints(e.target.value)}
                placeholder="e.g. avoid the trust structure entirely"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="p-duration">
                Time limit (minutes, optional)
              </label>
              <input
                id="p-duration"
                type="number"
                min={1}
                value={maxDuration}
                onChange={(e) => setMaxDuration(e.target.value)}
                placeholder="e.g. 30"
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={generating}
              onClick={() => void generate()}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-stone-950 hover:bg-amber-700 disabled:opacity-40"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />}
              {generating ? 'Building plan…' : 'Generate plan'}
            </button>
          </div>
          {generating ? (
            <p className="text-xs text-stone-500">
              Building from the prior statements, accepted contradictions and matter context — this
              takes up to a minute.
            </p>
          ) : null}
        </div>
      ) : null}

      {plans.length === 0 && !showForm ? (
        <p className="text-sm text-stone-600">No plans yet.</p>
      ) : (
        plans.map((p) => (
          <Link
            key={p.id}
            to={`/plans/${p.id}`}
            className="flex items-center justify-between rounded-md border border-stone-800/60 bg-stone-900/40 px-3 py-2 text-sm text-stone-200 hover:border-amber-700/60"
          >
            <span className="min-w-0 truncate">{p.title || 'Untitled plan'}</span>
            <span className="flex shrink-0 items-center gap-2 text-[10px] uppercase tracking-[0.15em]">
              {p.citationsFlagged > 0 ? (
                <span className="text-amber-400">{p.citationsFlagged} unverified cites</span>
              ) : null}
              <span
                className={
                  p.status === 'live'
                    ? 'text-red-300'
                    : p.status === 'rehearsed'
                      ? 'text-emerald-300'
                      : 'text-stone-500'
                }
              >
                {p.status}
              </span>
              <span className="text-stone-600">~{p.estimatedDuration}m</span>
            </span>
          </Link>
        ))
      )}
    </div>
  );
}
